/**
 * @fileoverview UserInfoHeader — top row of the UserAccountMenu popover.
 * Renders a `PrincipalAvatar` (sized up + with the `variant="you"` ring to
 * mark "this is *you*"), the user's full name as a link to their settings
 * page, and a muted "(username)" line directly below the name.
 *
 * Foundry mapping: `user-account-menu__user-info__*` wrapping a
 * `__name-wrapper` + `__name-link` + `__username` stack next to the
 * principal avatar (rendered with the `you-avatar` modifier).
 */

import type { MouseEvent, ReactElement } from "react";
import { PrincipalAvatar } from "../carbon/principal-avatar";

interface UserInfoHeaderProps {
  /** Display name shown as the primary link. */
  name: string;
  /** Email or login handle shown in parens beneath the name. */
  username?: string;
  /** Initials used by the inline `PrincipalAvatar`. */
  initials: string;
  /** Optional user avatar image. */
  imageUrl?: string;
  /** Settings/profile destination for the name link. */
  href?: string;
  /** Click handler for the name link. Receives the event so SPA consumers
   *  can `preventDefault()` and route client-side instead of full-reloading. */
  onNameClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  className?: string;
}

export function UserInfoHeader({
  name,
  username,
  initials,
  imageUrl,
  href,
  onNameClick,
  className,
}: UserInfoHeaderProps): ReactElement {
  return (
    <div data-testid="user-info-header" className={["bp-user-info-header", className].filter(Boolean).join(" ")}>
      <PrincipalAvatar
        initials={initials}
        imageUrl={imageUrl}
        variant="you"
        ariaLabel={`${name} avatar`}
      />
      <div className="bp-user-info-header-name-wrapper">
        {href || onNameClick ? (
          <a
            className="bp-user-info-header-name-link"
            href={href ?? "#"}
            onClick={onNameClick}
          >
            {name}
          </a>
        ) : (
          <span className="bp-user-info-header-name-link">{name}</span>
        )}
        {username && (
          <span className="bp-user-info-header-username">({username})</span>
        )}
      </div>
    </div>
  );
}
